import { useQuery } from '@tanstack/react-query';
import {
  SourcingAggregateSpendResponse,
  SourcingService,
} from 'api-clients/monolith';
import _range from 'lodash/range';
import { PropsWithChildren, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

interface AggregateSpendHoverCardProps extends PropsWithChildren {
  openingSlug: string;
}

export function AggregateSpendHoverCard({
  children,
  openingSlug,
}: AggregateSpendHoverCardProps) {
  const [open, setOpen] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: [...queryKeys.dashboard.aggregateSpend, openingSlug],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsAggregateSpend(openingSlug),
    enabled: open,
  });

  const openings = useMemo(() => data?.aggregate_spend ?? [], [data]);

  return (
    <HoverCard open={open} onOpenChange={setOpen}>
      <HoverCardTrigger>{children}</HoverCardTrigger>
      <HoverCardContent align="start" className="w-80">
        <div className="flex flex-col items-start justify-center gap-2 py-1.5">
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage defaultMessage="This number is the aggregate of multiple openings within the hiring workflow" />
          </Typography>

          <div className="flex w-full items-center justify-between py-1.5">
            <Typography variant="caption">
              <FormattedMessage defaultMessage="Openings" />
            </Typography>
            <Typography variant="caption">
              <FormattedMessage defaultMessage="Spend" />
            </Typography>
          </div>

          <AggregateSpendRows openings={openings} isFetching={isFetching} />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function AggregateSpendRows({
  openings,
  isFetching,
}: {
  openings: SourcingAggregateSpendResponse['aggregate_spend'];
  isFetching: boolean;
}) {
  if (isFetching)
    return (
      <>
        {_range(2).map(index => (
          <div
            key={index}
            className="flex w-full items-center justify-between py-1.5"
          >
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-5" />
          </div>
        ))}
      </>
    );

  return (
    <>
      {openings.map(opening => {
        if (!opening.spend) return null;

        return (
          <div
            key={opening.external_id}
            className="flex w-full items-center justify-between py-1.5"
          >
            <Typography
              variant="body1"
              className="!text-fx-neutral-600 w-[90%]"
            >
              {opening.name}
            </Typography>
            <Typography variant="body2" className="!text-fx-neutral-600">
              {opening.spend}
            </Typography>
          </div>
        );
      })}
    </>
  );
}
